import { capitalisedTermForCompany } from "../../copy/sharedCopy";
import { useDueToExpireSettings } from "../../hooks/useDueToExpireSettings";
import {
    DriverAddressInformation,
    DriverVehicleInformation,
} from "../../models/api/admin/driverDetails";
import { type GreyFleetTableData } from "../../models/api/admin/greyFleet";
import { CompanyRiskProfile } from "../../models/app/admin/company";
import { AllowedColumnIdsRecord } from "../../models/app/admin/driver";
import { driverPointsColor } from "../../utils/driverPointsColour";
import { formatDateToString } from "../../utils/formatDateToString";
import {
    DriverAddressTableAllowedIds,
    driverAddressTableContent,
    driverAddressTableHeaders,
} from "../ReusableComponents/SharedTableContent/driverAddressTableContent";
import {
    DriverVehicleTableAllowedIds,
    driverVehicleTableContent,
    driverVehicleTableHeaders,
} from "../ReusableComponents/SharedTableContent/driverVehicleTableContent";
import {
    type TableContent,
    type TableHeader,
} from "../ReusableComponents/Table";
import UserIcon from "../UserIcon";
import HighlightedDate from "../ReusableComponents/HighlightedDate";
import { DriverListOrderByOptions } from "../../models/api/admin/driver";
export const greyFleetTableAllowedIdsEn = {
    driverId: "driverId",
    name: "name",
    email: "emailAddress",
    dateOfBirth: "dateOfBirth",
    declarationStatus: "declarationStatus",
    declarationExpiry: "declarationExpires",
    declarationLastSent: "declarationLastSent",
    company: "client",
    department: "department",
    jobRole: "jobRole",
    employeeNumber: "employeeNumber",
    endorsementCodes: "endorsementCodes",
    licenceType: "licenceType",
    licenceStatus: "licenceStatus",
    externalReference: "externalReference",
    licenceNumber: "licenceNumber",
    licenceOrigin: "licenceOrigin",
    licenceExpires: "licenceExpires",
    licenceCategories: "licenceCategories",
    categoryExpiryDates: "categoryExpiryDates",
    expiringCategories: "expiringCategories",
    expiredCategories: "expiredCategories",
    licenceNextCheckDate: "nextCheck",
    licenceLastCheckedAtDate: "lastChecked",
    previousLicenceChecks: "previousChecks",
    points: "points",
    linkedDriverStatus: "linkedDriverStatus",
    mainDriverName: "mainDriverName",
    comments: "comments",
    cpcLgvExpiryDate: "cpcLgvExpiryDate",
    cpcPcvExpiryDate: "cpcPcvExpiryDate",
    tachoCardExpiryDate: "tachoCardExpiryDate",
    vehicleInsuranceExpiry: "vehicleInsuranceExpiry",
    GreyFleetInviteEmailLastSent: "GreyFleetInviteEmailLastSent",
} as const satisfies AllowedColumnIdsRecord;

type AllowedIds =
    | (typeof greyFleetTableAllowedIdsEn)[keyof typeof greyFleetTableAllowedIdsEn]
    | DriverAddressTableAllowedIds
    | DriverVehicleTableAllowedIds;

export enum GreyfleetMenuGroups {
    PersonalInformation = "Personal information",
    Declaration = "Declaration",
    Employment = "Employment",
    DrivingLicence = "Driving licence",
    VehicleInformation = "Vehicle information",
    Address = "Address",
    LinkedDriver = "Linked driver",
    Comments = "Comments",
}

export const greyfleetMenuGroups: GreyfleetMenuGroups[] = (
    Object.keys(GreyfleetMenuGroups) as Array<keyof typeof GreyfleetMenuGroups>
).map((key) => GreyfleetMenuGroups[key]);

type GreyFleetTableContents = TableContent<GreyFleetTableData, AllowedIds>;

export const initGreyFleetTableHeaders: TableHeader<AllowedIds>[] = [
    {
        title: "Driver Id",
        id: "driverId",
        menuGroup: GreyfleetMenuGroups.PersonalInformation,
        hide: true,
    },
    {
        title: "Name",
        id: "name",
        menuGroup: GreyfleetMenuGroups.PersonalInformation,
        hide: false,
        orderBy: DriverListOrderByOptions.LastName,
    },
    {
        title: "Email",
        id: "emailAddress",
        hide: false,
        orderBy: DriverListOrderByOptions.EmailAddress,
        menuGroup: "Driver Details",
    },
    {
        title: "Date of birth",
        id: "dateOfBirth",
        hide: true,
        orderBy: DriverListOrderByOptions.DateOfBirth,
        menuGroup: GreyfleetMenuGroups.PersonalInformation,
    },
    {
        title: "Declaration Status",
        id: "declarationStatus",
        hide: true,
        menuGroup: GreyfleetMenuGroups.Declaration,
    },
    {
        title: "Declaration expires",
        id: "declarationExpires",
        hide: true,
        menuGroup: GreyfleetMenuGroups.Declaration,
    },
    {
        title: "Declaration Last Sent",
        id: "declarationLastSent",
        hide: true,
        menuGroup: GreyfleetMenuGroups.Declaration,
    },
    {
        title: capitalisedTermForCompany,
        id: "client",
        menuGroup: GreyfleetMenuGroups.Employment,
        orderBy: DriverListOrderByOptions.Company,
        hide: false,
    },
    {
        title: "Department",
        id: "department",
        menuGroup: "Employment",
        orderBy: DriverListOrderByOptions.Department,
        hide: false,
    },
    {
        title: "Job Role",
        id: "jobRole",
        hide: true,
        menuGroup: GreyfleetMenuGroups.Employment,
    },
    {
        title: "Employee Number",
        id: "employeeNumber",
        hide: true,
        orderBy: DriverListOrderByOptions.EmployeeId,
        menuGroup: GreyfleetMenuGroups.Employment,
    },
    {
        title: "Licence type",
        id: "licenceType",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Licence status",
        id: "licenceStatus",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Endorsement Codes",
        id: "endorsementCodes",
        hide: true,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "External Reference",
        id: "externalReference",
        hide: true,
        orderBy: DriverListOrderByOptions.ExternalReference,
        menuGroup: GreyfleetMenuGroups.Employment,
    },
    {
        title: "Licence Number",
        id: "licenceNumber",
        hide: true,
        orderBy: DriverListOrderByOptions.LicenceNumber,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "Licence Origin",
        id: "licenceOrigin",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Licence Expires",
        id: "licenceExpires",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "All Licence Categories",
        id: "licenceCategories",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Category Expiry Dates",
        id: "categoryExpiryDates",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Expiring Categories",
        id: "expiringCategories",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Expired Categories",
        id: "expiredCategories",
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "CPC LGV Expiry Date",
        id: "cpcLgvExpiryDate",
        hide: true,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "CPC PCV Expiry Date",
        id: "cpcPcvExpiryDate",
        hide: true,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "Tacho Card Expiry Date",
        id: "tachoCardExpiryDate",
        hide: true,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "Points",
        id: "points",
        menuGroup: "Driving licence",
        hide: false,
    },
    {
        title: "Last Checked",
        id: "lastChecked",
        hide: true,
        orderBy: DriverListOrderByOptions.LastChecked,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "Previous Checks",
        id: "previousChecks",
        hide: true,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "Next Check",
        id: "nextCheck",
        hide: true,
        orderBy: DriverListOrderByOptions.NextCheck,
        menuGroup: GreyfleetMenuGroups.DrivingLicence,
    },
    {
        title: "Linked Driver Status",
        id: "linkedDriverStatus",
        hide: true,
        menuGroup: GreyfleetMenuGroups.LinkedDriver,
    },
    {
        title: "Main Driver Name",
        id: "mainDriverName",
        hide: true,
        menuGroup: GreyfleetMenuGroups.LinkedDriver,
    },
    {
        title: "Comments",
        id: "comments",
        hide: true,
        menuGroup: GreyfleetMenuGroups.Comments,
    },
    {
        title: "Grey Fleet Invite Last Sent",
        id: "GreyFleetInviteEmailLastSent",
        hide: true,
        menuGroup: GreyfleetMenuGroups.VehicleInformation,
    },
    ...driverVehicleTableHeaders,
    ...driverAddressTableHeaders,
];

export function initGreyFleetTableContents(
    drivers?: GreyFleetTableData[] | null,
    companyRiskProfile?: CompanyRiskProfile[]
): GreyFleetTableContents[] {
    const { getDateHighlightStyle } = useDueToExpireSettings();

    const tableContents = new Array<GreyFleetTableContents>(0);
    drivers?.map((driver) => {
        const address: DriverAddressInformation = {
            postCode: driver.postCode,
            addressMatch: driver.addressMatch,
        };
        const vehicles: DriverVehicleInformation = {
            vehicleId: driver.vehicleId,
            vehicleRegistration: driver.vehicleRegistration,
            manufacturer: driver.manufacturer,
            euroRating: driver.euroRating,
            model: driver.model,
            yearOfManufacture: driver.yearOfManufacture,
            motExpiry: driver.motExpiry,
            engineCapacity: driver.engineCapacity,
            roadTaxExpiry: driver.roadTaxExpiry,
            co2Emissions: driver.co2Emissions,
            fuelType: driver.fuelType,
            insuranceType: driver.insuranceType,
            vehicleInsuranceUploaded: driver.vehicleInsuranceUploaded,
            vehicleInsuranceVerified: driver.vehicleInsuranceVerified,
            vehicleInsuranceExpiry: driver.vehicleInsuranceExpiry,
            businessCover: driver.businessCover,
        };
        tableContents.push({
            rowId: driver.driverId,
            rowData: driver,
            cellData: [
                {
                    id: "driverId",
                    content: <p>{driver.driverId}</p>,
                },
                {
                    id: "name",
                    content: (
                        <div className="flex items-center gap-2">
                            <UserIcon
                                firstName={driver.firstName}
                                lastName={driver.lastName}
                                size="small"
                            />
                            <p>{`${driver.firstName} ${driver.lastName}`}</p>
                        </div>
                    ),
                },
                {
                    id: "emailAddress",
                    content: <p>{driver.emailAddress}</p>,
                },
                {
                    id: "dateOfBirth",
                    content: (
                        <p>
                            {driver.dateOfBirth
                                ? formatDateToString(
                                      new Date(driver.dateOfBirth)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "declarationStatus",
                    content: <p>{driver.declarationStatus}</p>,
                },
                {
                    id: "declarationLastSent",
                    content: (
                        <p>
                            {driver.declarationLastSent
                                ? formatDateToString(
                                      new Date(driver.declarationLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "declarationExpires",
                    content: (
                        <p>
                            {driver.declarationExpiry
                                ? formatDateToString(
                                      new Date(driver.declarationExpiry)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "client",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.companyName}
                        </p>
                    ),
                },
                {
                    id: "department",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.departmentName}
                        </p>
                    ),
                },
                {
                    id: "jobRole",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.jobRole}
                        </p>
                    ),
                },
                {
                    id: "employeeNumber",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.employeeNumber}
                        </p>
                    ),
                },
                {
                    id: "endorsementCodes",
                    content: <p>{driver.endorsementCodes?.join(", ")}</p>,
                },
                {
                    id: "licenceType",
                    content: <p>{driver.licenceType}</p>,
                },
                {
                    id: "licenceStatus",
                    content: <p>{driver.licenceStatus}</p>,
                },
                {
                    id: "externalReference",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.externalReference}
                        </p>
                    ),
                },
                {
                    id: "licenceNumber",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.licenceNumber}
                        </p>
                    ),
                },
                {
                    id: "licenceOrigin",
                    content: <p>{driver.licenceOrigin}</p>,
                },
                {
                    id: "licenceExpires",
                    content: (
                        <p>
                            {driver.licenceExpires
                                ? formatDateToString(
                                      new Date(driver.licenceExpires)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "licenceCategories",
                    content: <p>{driver.licenceCategories?.join(", ")}</p>,
                },
                {
                    id: "categoryExpiryDates",
                    content: (
                        <p>
                            {driver.categoryExpiryDates
                                ?.map((dateString, index) => {
                                    if (!dateString) return "";

                                    const categoryExpiryDatesLength =
                                        driver?.categoryExpiryDates?.length ??
                                        0;

                                    const showTrailingComma =
                                        index < categoryExpiryDatesLength - 1;

                                    const style = getDateHighlightStyle(
                                        dateString,
                                        "dueToExpireLicenceCategoryDays"
                                    );

                                    return (
                                        <HighlightedDate
                                            key={dateString}
                                            style={style}
                                            date={dateString}
                                            showTrailingComma={
                                                showTrailingComma
                                            }
                                        />
                                    );
                                })
                                .filter(Boolean)}
                        </p>
                    ),
                },
                {
                    id: "expiringCategories",
                    content: <p>{driver.expiringCategories?.join(", ")}</p>,
                },
                {
                    id: "expiredCategories",
                    content: <p>{driver.expiredCategories?.join(", ")}</p>,
                },                            
                {
                    id: "cpcLgvExpiryDate",
                    content: <p>
                                {driver.cpcLgvExpiryDate ? formatDateToString(
                                      new Date(driver.cpcLgvExpiryDate)
                                  ) : ""}
                            </p>,
                },
                {
                    id: "cpcPcvExpiryDate",
                    content: <p>
                                {driver.cpcPcvExpiryDate ? formatDateToString(
                                      new Date(driver.cpcPcvExpiryDate)
                                  ) : ""}
                            </p>,
                },
                {
                    id: "tachoCardExpiryDate",
                    content: <p>
                                {driver.tachoCardExpiryDate ? formatDateToString(
                                      new Date(driver.tachoCardExpiryDate)
                                  ) : ""}
                            </p>,
                },
                {
                    id: "points",
                    content: (
                        <div className="pl-2">
                            <div
                                className={`h-7 w-7 rounded-full ${driverPointsColor(
                                    driver.totalPoints,
                                    companyRiskProfile
                                )} flex items-center justify-center`}
                            >
                                {driver.totalPoints ?? "-"}
                            </div>
                        </div>
                    ),
                },
                {
                    id: "lastChecked",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.licenceLastCheckedAtDate
                                ? formatDateToString(
                                      new Date(driver.licenceLastCheckedAtDate)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "previousChecks",
                    content: (
                        <div className="flex flex-col gap-1">
                            <p className="max-w-[15rem] truncate">
                                {driver.previousLicenceCheckDateOne
                                    ? formatDateToString(
                                          new Date(
                                              driver.previousLicenceCheckDateOne
                                          )
                                      )
                                    : ""}
                            </p>
                            <p className="max-w-[15rem] truncate">
                                {driver.previousLicenceCheckDateTwo
                                    ? formatDateToString(
                                          new Date(
                                              driver.previousLicenceCheckDateTwo
                                          )
                                      )
                                    : ""}
                            </p>
                            <p className="max-w-[15rem] truncate">
                                {driver.previousLicenceCheckDateThree
                                    ? formatDateToString(
                                          new Date(
                                              driver.previousLicenceCheckDateThree
                                          )
                                      )
                                    : ""}
                            </p>
                        </div>
                    ),
                },
                {
                    id: "nextCheck",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.licenceNextCheckDate
                                ? formatDateToString(
                                      new Date(driver.licenceNextCheckDate)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "linkedDriverStatus",
                    content: <p>{driver.linkedDriverStatus}</p>,
                },
                {
                    id: "mainDriverName",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.mainDriverName}
                        </p>
                    ),
                },
                {
                    id: "comments",
                    content: (
                        <div className="pl-6">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-S2D-light-green-80">
                                {driver.comments}
                            </div>
                        </div>
                    ),
                },
                {
                    id: "GreyFleetInviteEmailLastSent",
                    content: (
                        <p>
                            {driver.greyFleetInviteLastSent
                                ? formatDateToString(
                                      new Date(driver.greyFleetInviteLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                ...driverAddressTableContent(address),
                ...driverVehicleTableContent(vehicles),
            ],
        });
    });
    return tableContents;
}
